import { useState, useEffect, type FormEvent } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Btn, InputField, Alert } from '@/presentation/component/ui';
import { useAuth } from '@/presentation/hook/useAuth';
import { resolveErrorKey } from '@/infrastructure/util/error';

const LOGIN_ERROR_KEYS = { 401: 'auth.invalidCredentials' };

export const LoginPage = () => {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const { login, isLoading, isAuthenticated, redirectPath, setRedirectPath, clearRedirectPath } =
    useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const from = searchParams.get('from');
    if (from) {
      setRedirectPath(from);
    }
  }, [location.search, setRedirectPath]);

  useEffect(() => {
    if (isAuthenticated && !isLoading) {
      const searchParams = new URLSearchParams(location.search);
      const fromParam = searchParams.get('from');
      const destinationPath = redirectPath || fromParam || '/formation';

      const timeout = setTimeout(() => {
        clearRedirectPath();
        navigate(destinationPath, { replace: true });
      }, 100);

      return () => clearTimeout(timeout);
    }
  }, [isAuthenticated, isLoading, redirectPath, clearRedirectPath, navigate, location.search]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');

    try {
      await login(email, password);
    } catch (e) {
      setError(t(resolveErrorKey(e, 'auth.invalidCredentials', LOGIN_ERROR_KEYS)));
    }
  };

  const signupLink = redirectPath ? `/signup?from=${encodeURIComponent(redirectPath)}` : '/signup';

  return (
    <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <h2 className="text-3xl font-bold text-[var(--color-ink)]">{t('auth.loginTitle')}</h2>
          <p className="mt-2 text-[var(--color-ink-soft)]">
            {t('auth.orText')}{' '}
            <Link
              to={signupLink}
              className="text-[var(--color-accent)] hover:text-[var(--color-accent-hover)]"
            >
              {t('auth.loginSubtitle')}
            </Link>
          </p>
        </div>

        <form
          className="mt-8 space-y-6 bg-[var(--color-surface)] p-8 rounded-lg shadow-sm border border-[var(--color-line)]"
          onSubmit={handleSubmit}
        >
          {error && (
            <Alert kind="bad" onClose={() => setError('')}>
              {error}
            </Alert>
          )}

          <InputField
            label={t('contact.email')}
            id="email"
            name="email"
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder={t('contact.emailPlaceholder')}
          />

          <InputField
            label={t('auth.password')}
            id="password"
            name="password"
            type="password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder={t('auth.passwordPlaceholder')}
          />

          <Btn
            kind="primary"
            type="submit"
            isLoading={isLoading}
            disabled={isLoading}
            className="w-full"
          >
            {t('buttons.signIn')}
          </Btn>

          <div className="text-center">
            <Link
              to="#"
              className="text-sm text-[var(--color-accent)] hover:text-[var(--color-accent-hover)]"
            >
              {t('auth.forgotPassword')}
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
};
